<template>
  <div
    class="container mx-auto mt-12 md:mt-18 lg:mt-20 xl:mt-24 md:mb-18 mb-12 px-4"
  >
    <div>
      <h1 class="text-center mx-auto max-w-6xl text-3xl lg:text-5xl mb-10">
        {{ dataLocale.data.title }}
      </h1>
      <p
        class="text-lg md:text-xl text-center max-w-4xl mx-auto"
        v-html="dataLocale.data.description"
      ></p>
    </div>
  </div>
  <div class="mt-24 bg-soft py-8 bg-header-pattern xs:px-0 px-4">
    <div class="container mx-auto">
      <PagesPricingButtonType :type="type" @changeType="changeType" />
      <p class="text-center py-5" v-html="dataLocale.data.note"></p>
      <div class="max-w-6xl mx-auto grid mt-12 grid-cols-12 xl:gap-0 gap-4 rounded-xl">
        <div
          class="col-span-12 sm:col-span-6 xl:col-span-4 xl:mb-0 xl:mt-0 mb-10 border-gray-200 bg-white"
          v-for="(plan, i) in plans"
          :key="i"
        >
          <PagesPricingPlan :type="type" :plan="plan" />
        </div>
      </div>
    </div>
  </div>
  <PagesPricingEnterpriseContact />
  <PartialsTrackQrWidget />
  <PagesPricingFaq />
</template>
<script lang="ts" setup>
import DataLocale from "~~/config/interface/DataLocale";

const runtimeConfig = useRuntimeConfig();
const type = ref("annually");
const changeType = (data: string) => {
  type.value = data;
};
const res = await useAsyncData(async () => {
  const res: Array<{
    name: string;
    monthly_price: number;
    description: string;
    personal_yearly_price: number;
    limit: {
      scans: number;
      users: number;
      qrcodes: number;
      support: boolean;
      analytic: {
        range: boolean;
        device: boolean;
        export: boolean;
        social: boolean;
        widget: boolean;
        location: boolean;
      };
      hide_logo: boolean;
      enable_api: boolean;
      integration: {
        ecommerce: number | string;
        google_sheet: number | string;
      };
      vip_template: boolean;
      other_analytic: boolean;
    };
  }> = await $fetch(runtimeConfig.public.browserapi + "/api/plan-information");
  return res;
});
const plans = res.data.value;

const dataLocale = new DataLocale([
  {
    locale: "en",
    title: "Grow with QRDove",
    description:
      " The ultimate solution for managing <b>QR codes</b>. <br>Start smart and scale efficiently with our advanced features and powerful tools. Stay ahead of the game and take your business to the next level with us",
    note: 'Sign up for an account now <span class="font-esbuild font-bold text-lg">without credit</span>. You will be generated up to <span class="text-success underline font-bold">2 QR codes</span> for a <b>free account</b>.',
  },
  {
    locale: "vi",
    title: "Phát triển với QRDove",
    description:
      "Giải pháp tối ưu cho quản lý <b>mã QR</b>. <br>Bắt đầu thông minh và mở rộng hiệu quả với các tính năng tiên tiến và công cụ mạnh mẽ của chúng tôi. Vượt xa đối thủ cạnh tranh và đưa doanh nghiệp của bạn lên tầm cao mới với QRDove.",
    note: 'Đăng ký tài khoản ngay bây giờ <span class="font-esbuild font-bold text-lg">không cần thẻ tín dụng</span>. Bạn sẽ được tạo ra tối đa <span class="text-success underline font-bold">2 mã QR</span> cho một <b>tài khoản miễn phí</b>.',
  },
  {
    locale: "de",
    title: "Wachsen Sie mit QRDove",
    description:
      "Die ultimative Lösung zur Verwaltung von <b>QR-Codes</b>. <br>Starten Sie intelligent und skalieren Sie effizient mit unseren fortschrittlichen Funktionen und leistungsstarken Tools. Bleiben Sie immer einen Schritt voraus und bringen Sie Ihr Unternehmen mit QRDove auf die nächste Stufe.",
    note: 'Melden Sie sich jetzt für ein Konto an <span class="font-esbuild font-bold text-lg">ohne Kredit</span>. Sie erhalten bis zu <span class="text-success underline font-bold">2 QR-Codes</span> für ein <b>kostenloses Konto</b>.',
  },
  {
    locale: "fr",
    title: "Grandissez avec QRDove",
    description:
      "La solution ultime pour gérer les <b>codes QR</b>. <br>Commencez de manière intelligente et évoluez efficacement avec nos fonctionnalités avancées et nos outils puissants. Restez en avance sur la concurrence et faites passer votre entreprise au niveau supérieur avec QRDove.",
    note: `Inscrivez-vous maintenant pour un compte <span class="font-esbuild font-bold text-lg">sans crédit</span>. Vous pourrez générer jusqu'à <span class="text-success underline font-bold">2 codes QR</span> pour un <b>compte gratuit</b>.`,
  },
  {
    locale: "es",
    title: "Crecer con QRDove",
    description:
      "La solución definitiva para gestionar <b>códigos QR</b>. <br>Comience de forma inteligente y escale eficientemente con nuestras avanzadas características y herramientas poderosas. Manténgase por delante del juego y lleve su negocio al siguiente nivel con QRDove.",
    note: 'Regístrese ahora para obtener una cuenta <span class="font-esbuild font-bold text-lg">sin crédito</span>. Se generarán hasta <span class="text-success underline font-bold">2 códigos QR</span> para una <b>cuenta gratuita</b>.',
  },
  {
    locale: "pt",
    title: "Cresça com o QRDove",
    description:
      "A solução definitiva para gerenciar <b>códigos QR</b>. <br>Comece de forma inteligente e aumente sua eficiência com nossos recursos avançados e ferramentas poderosas. Mantenha-se à frente do jogo e leve seu negócio ao próximo nível com o QRDove.",
    note: 'Inscreva-se agora para uma conta <span class="font-esbuild font-bold text-lg">sem crédito</span>. Serão gerados até <span class="text-success underline font-bold">2 códigos QR</span> para uma <b>conta gratuita</b>.',
  },
  {
    locale: "it",
    title: "Cresci con QRDove",
    description:
      "La soluzione definitiva per gestire i <b>codici QR</b>. <br>Inizia in modo intelligente e scala in modo efficiente con le nostre funzionalità avanzate e gli strumenti potenti. Resta sempre un passo avanti e porta la tua attività al livello successivo con QRDove.",
    note: 'Registrati ora per un account <span class="font-esbuild font-bold text-lg">senza credito</span>. Saranno generati fino a <span class="text-success underline font-bold">2 codici QR</span> per un <b>account gratuito</b>.',
  },
  {
    locale: "nl",
    title: "Groei met QRDove",
    description:
      "De ultieme oplossing voor het beheren van <b>QR-codes</b>. <br>Begin slim en schaal efficiënt met onze geavanceerde functies en krachtige tools. Blijf voor op de concurrentie en breng je bedrijf naar een hoger niveau met QRDove.",
    note: 'Meld u nu aan voor een account <span class="font-esbuild font-bold text-lg">zonder creditcard</span>. U kunt tot <span class="text-success underline font-bold">2 QR-codes</span> genereren voor een <b>gratis account</b>.',
  },
  {
    locale: "ja",
    title: "QRDoveで成長しよう",
    description:
      "最先端の機能や強力なツールでスマートにスタートし、効率的にスケールアップできる、<b>QRコード</b>管理の究極のソリューション。QRDoveで常に先を行くことで、あなたのビジネスを次のレベルに導きましょう。",
    note: '<span class="font-esbuild font-bold text-lg">クレジットカードなしで</span>今すぐアカウントにサインアップしてください。無料アカウントでは、<span class="text-success underline font-bold">最大2つのQRコード</span>を生成できます。',
  },
  {
    locale: "zh",
    title: "与QRDove一起成长",
    description:
      "管理<b>二维码</b>的终极解决方案。智能起步，高效扩展，使用我们的先进功能和强大工具。与我们并肩前进，将您的业务提升到一个新的水平。",
    note: '现在注册账户<span class="font-esbuild font-bold text-lg">无需信用卡</span>。免费账户最多可以生成<span class="text-success underline font-bold">2个QR码</span>。',
  },

  {
    locale: "tr",
    title: "QRDove ile Büyüyün",
    description:
      "<b>QR kodlarını</b> yönetmek için nihai çözüm. <br>Gelişmiş özelliklerimiz ve güçlü araçlarımızla akıllıca başlayın ve verimli bir şekilde ölçeklenin. Oyunda önde kalın ve işinizi bizimle bir üst seviyeye taşıyın",
    note: 'Şimdi bir hesap için <span class="font-esbuild font-bold text-lg">kredi kartı olmadan</span> kaydolun. <b>Ücretsiz bir hesap</b> için en fazla <span class="text-success underline font-bold">2 QR kodu</span> oluşturulacaktır.',
  },
  {
    locale: "hi",
    title: "QRDove के साथ आगे बढ़ें",
    description:
      "<b>QR कोड</b> को प्रबंधित करने का अंतिम समाधान। <br>हमारी उन्नत सुविधाओं और शक्तिशाली उपकरणों के साथ स्मार्ट शुरुआत करें और कुशलतापूर्वक स्केल करें। खेल में आगे रहें और हमारे साथ अपने व्यवसाय को अगले स्तर पर ले जाएं",
    note: 'अभी <span class="font-esbuild font-bold text-lg">बिना क्रेडिट</span> के एक खाते के लिए साइन अप करें। आपको <b>मुफ्त खाते</b> के लिए <span class="text-success underline font-bold">2 QR कोड</span> तक जेनरेट किए जाएंगे।',
  },
  {
    locale: "id",
    title: "Berkembang dengan QRDove",
    description:
      "Solusi utama untuk mengelola <b>kode QR</b>. <br>Mulailah dengan cerdas dan tingkatkan skala secara efisien dengan fitur canggih dan alat kami yang hebat. Tetap unggul dalam permainan dan bawa bisnis Anda ke level berikutnya bersama kami",
    note: 'Daftar untuk akun sekarang <span class="font-esbuild font-bold text-lg">tanpa kartu kredit</span>. Anda akan dibuat hingga <span class="text-success underline font-bold">2 kode QR</span> untuk <b>akun gratis</b>.',
  },
  {
    locale: "th",
    title: "เติบโตไปกับ QRDove",
    description:
      "สุดยอดโซลูชันสำหรับการจัดการ<b>รหัส QR</b> <br>เริ่มต้นอย่างชาญฉลาดและขยายขนาดอย่างมีประสิทธิภาพด้วยคุณสมบัติขั้นสูงและเครื่องมืออันทรงพลังของเรา นำหน้าเกมและนำธุรกิจของคุณไปสู่อีกระดับกับเรา",
    note: 'ลงทะเบียนบัญชีตอนนี้ <span class="font-esbuild font-bold text-lg">โดยไม่ต้องใช้เครดิต</span> คุณจะได้รับรหัส QR สูงสุด <span class="text-success underline font-bold">2 รหัส</span> สำหรับ<b>บัญชีฟรี</b>',
  },
]);

import moment from "moment";

const seoheader = new DataLocale([
  {
    locale: "en",
    title:
      "Scan Smarter, Save More: Affordable QR Code Pricing for Your Business",
    description:
      "Our QR code generator offers various versions with unique features and benefits. Select the plan that suits your needs and maximize its value for your specific use case",
  },
  {
    locale: "vi",
    title:
      "Quét thông minh, tiết kiệm hơn: Giá QR code phù hợp cho doanh nghiệp của bạn",
    description:
      "Trình tạo QR code của chúng tôi cung cấp nhiều phiên bản với các tính năng và lợi ích độc đáo. Chọn gói phù hợp với nhu cầu của bạn và tối đa hóa giá trị của nó cho trường hợp sử dụng cụ thể của bạn",
  },
  {
    locale: "fr",
    title:
      "Numériser intelligemment, économiser plus : Tarification QR code abordable pour votre entreprise",
    description:
      "Notre générateur de QR code propose différentes versions avec des fonctionnalités et des avantages uniques. Choisissez le plan qui convient à vos besoins et maximisez sa valeur pour votre cas d'utilisation spécifique",
  },
  {
    locale: "de",
    title:
      "Smart scannen, mehr sparen: Erschwingliche QR-Code-Preise für Ihr Unternehmen",
    description:
      "Unser QR-Code-Generator bietet verschiedene Versionen mit einzigartigen Funktionen und Vorteilen. Wählen Sie den Plan, der Ihren Anforderungen entspricht, und maximieren Sie seinen Wert für Ihren spezifischen Anwendungsfall",
  },
  {
    locale: "es",
    title:
      "Escanea de manera inteligente y ahorra más: precios accesibles de códigos QR para tu negocio",
    description:
      "Nuestro generador de códigos QR ofrece varias versiones con características y beneficios únicos. Selecciona el plan que se adapte a tus necesidades y maximiza su valor para tu caso de uso específico.",
  },

  {
    locale: "pt",
    title:
      "Digitalize de forma inteligente e economize mais: preços acessíveis de códigos QR para o seu negócio",
    description:
      "Nosso gerador de códigos QR oferece várias versões com recursos e benefícios exclusivos. Selecione o plano que atenda às suas necessidades e maximize seu valor para seu caso de uso específico",
  },

  {
    locale: "nl",
    title:
      "Slimmer scannen, meer besparen: betaalbare QR-code prijzen voor uw bedrijf",
    description:
      "Onze QR-code generator biedt verschillende versies met unieke functies en voordelen. Kies het plan dat bij uw behoeften past en maximaliseer de waarde ervan voor uw specifieke gebruikscase",
  },

  {
    locale: "it",
    title:
      "Scansiona in modo intelligente, risparmia di più: Prezzi accessibili per codici QR per la tua azienda",
    description:
      "Il nostro generatore di codici QR offre diverse versioni con funzioni e vantaggi unici. Seleziona il piano che soddisfa le tue esigenze e massimizza il suo valore per il tuo caso d'uso specifico",
  },
  {
    locale: "ja",
    title:
      "スマートにスキャンして、より多くの節約：あなたのビジネスに適した手頃なQRコード価格",
    description:
      "当社のQRコードジェネレーターは、ユニークな機能や利点を備えたさまざまなバージョンを提供しています。ニーズに合わせたプランを選択し、特定のユースケースでの価値を最大化してください。",
  },

  {
    locale: "zh",
    title: "智能扫描，更省钱：适用于您的企业的实惠QR码定价",
    description:
      "我们的QR码生成器提供具有独特功能和优势的各种版本。选择适合您需求的计划，并将其在特定用例中的价值最大化",
  },
  {
    locale: "tr",
    title: "QRDove ile Büyüyün",
    description:
      "<b>QR kodlarını</b> yönetmek için nihai çözüm. <br>Gelişmiş özelliklerimiz ve güçlü araçlarımızla akıllıca başlayın ve verimli bir şekilde ölçeklenin. Oyunda önde kalın ve işinizi bizimle bir üst seviyeye taşıyın",
    note: 'Şimdi bir hesap için <span class="font-esbuild font-bold text-lg">kredi kartı olmadan</span> kaydolun. <b>Ücretsiz bir hesap</b> için en fazla <span class="text-success underline font-bold">2 QR kodu</span> oluşturulacaktır.',
  },
  {
    locale: "hi",
    title: "QRDove के साथ आगे बढ़ें",
    description:
      "<b>QR कोड</b> को प्रबंधित करने का अंतिम समाधान। <br>हमारी उन्नत सुविधाओं और शक्तिशाली उपकरणों के साथ स्मार्ट शुरुआत करें और कुशलतापूर्वक स्केल करें। खेल में आगे रहें और हमारे साथ अपने व्यवसाय को अगले स्तर पर ले जाएं",
    note: 'अभी <span class="font-esbuild font-bold text-lg">बिना क्रेडिट</span> के एक खाते के लिए साइन अप करें। आपको <b>मुफ्त खाते</b> के लिए <span class="text-success underline font-bold">2 QR कोड</span> तक जेनरेट किए जाएंगे।',
  },
  {
    locale: "id",
    title: "Berkembang dengan QRDove",
    description:
      "Solusi utama untuk mengelola <b>kode QR</b>. <br>Mulailah dengan cerdas dan tingkatkan skala secara efisien dengan fitur canggih dan alat kami yang hebat. Tetap unggul dalam permainan dan bawa bisnis Anda ke level berikutnya bersama kami",
    note: 'Daftar untuk akun sekarang <span class="font-esbuild font-bold text-lg">tanpa kartu kredit</span>. Anda akan dibuat hingga <span class="text-success underline font-bold">2 kode QR</span> untuk <b>akun gratis</b>.',
  },
  {
    locale: "th",
    title: "เติบโตไปกับ QRDove",
    description:
      "สุดยอดโซลูชันสำหรับการจัดการ<b>รหัส QR</b> <br>เริ่มต้นอย่างชาญฉลาดและขยายขนาดอย่างมีประสิทธิภาพด้วยคุณสมบัติขั้นสูงและเครื่องมืออันทรงพลังของเรา นำหน้าเกมและนำธุรกิจของคุณไปสู่อีกระดับกับเรา",
    note: 'ลงทะเบียนบัญชีตอนนี้ <span class="font-esbuild font-bold text-lg">โดยไม่ต้องใช้เครดิต</span> คุณจะได้รับรหัส QR สูงสุด <span class="text-success underline font-bold">2 รหัส</span> สำหรับ<b>บัญชีฟรี</b>',
  },
]);

const datajd = useDataJd();
const meta = useMeta();

const i18nHead = useLocaleHead({
  addSeoAttributes: {
    canonicalQueries: ["/"],
  },
});
const meta_tags = meta.metaTag({
  title: seoheader.data.title,
  description: seoheader.data.description,
  url: datajd.currentURL(),
  image: datajd.assets("/images/og/pricing.jpg"),
});
useHead({
  title: seoheader.data.title,
  meta: [
    { name: "description", content: seoheader.data.description },
    ...(i18nHead.value.meta || []),
    ...meta_tags,
  ],
  htmlAttrs: {
    lang: i18nHead.value.htmlAttrs!.lang,
  },
  link: [...(i18nHead.value.link || [])],
});
useSchemaOrg([
  defineWebPage({
    name: seoheader.data.title,
    datePublished: moment("2023-05-03").toISOString(),
    dateModified: moment().toISOString(),
    description: seoheader.data.description,
    inLanguage: datajd.inLanguages(true),
    url: datajd.currentURL(),
    image: datajd.assets("/images/og/pricing.jpg"),
  }),
  defineBreadcrumb({
    itemListElement: [
      {
        item: {
          "@id": datajd.currentHost(),
          name: "Home",
        },
      },
      { item: { name: "Pricing", "@id": datajd.currentHost() + "/pricing" } },
    ],
  }),
]);
</script>
